/**
 * TNCY-6b §b — `useOwnerMemberContracts` merged owner/member list for the
 * Contracts page.
 *
 * Mirrors the Projects `useOwnerMemberList` (TNCY-6) but for the Contracts
 * list, which the BE extended with `scope=member` + ownership metadata
 * (`ownership` / `ownerTenantName` / `role`) on `ContractListItem` in
 * projects/v1@2.4.0 (TNCY-4). Member rows are contracts of member projects
 * the caller was granted cross-tenant read access to via a `contract_*`
 * grant — the BE already projects them to the caller's role surface (XT-D22),
 * so the FE just renders what it gets.
 *
 * It fires BOTH scopes and merges them into one table (Christian 2026-06-23 —
 * ONE table, not tabs) via the shared `mergeOwnerMember` primitive:
 *
 *   - `scope=own`     — the caller's own contracts, SERVER-paginated.
 *   - `scope=member`  — the cross-tenant fan-out of GRANTED contracts, loaded
 *                       in FULL but BOUNDED (one page at `MEMBER_PAGE_SIZE`).
 *
 * Own-only fast path: when there are no member rows the hook returns the own
 * query's items + its server `total` verbatim, so a tenant with no
 * cross-tenant grants sees exactly the pre-TNCY-6b list.
 */
import { useMemo } from "react";
import {
  mergeOwnerMember,
  MEMBER_PAGE_SIZE,
} from "../shared/ownership/index.js";
import { useContractsListQuery } from "./useContracts.js";
import type {
  ContractListItem,
  ContractsSortDir,
  ContractsSortKey,
  ListContractsParams,
} from "./types.js";

export interface OwnerMemberContractsResult {
  rows: ContractListItem[];
  total: number;
  hasMemberRows: boolean;
  memberTruncated: boolean;
  isPending: boolean;
  isError: boolean;
}

function compareBy(
  key: ContractsSortKey,
  dir: ContractsSortDir,
): (a: ContractListItem, b: ContractListItem) => number {
  const sign = dir === "asc" ? 1 : -1;
  return (a, b) => {
    let av: number | string;
    let bv: number | string;
    switch (key) {
      case "contractCode":
      case "totalAssets":
        av = a[key];
        bv = b[key];
        break;
      case "progressPct":
        av = a.progressPct ?? -1;
        bv = b.progressPct ?? -1;
        break;
      case "projectName":
        av = a.projectName;
        bv = b.projectName;
        break;
      case "siteName":
        av = a.siteName ?? "";
        bv = b.siteName ?? "";
        break;
      case "customerName":
        av = a.customerTenantName ?? "";
        bv = b.customerTenantName ?? "";
        break;
      case "contractorName":
        av = a.contractorTenantName ?? "";
        bv = b.contractorTenantName ?? "";
        break;
      case "state":
        av = a.state;
        bv = b.state;
        break;
      default:
        av = a.contractCode;
        bv = b.contractCode;
    }
    if (typeof av === "number" && typeof bv === "number") {
      return (av - bv) * sign;
    }
    return String(av).localeCompare(String(bv)) * sign;
  };
}

/**
 * @param params  the page's list params (page/pageSize/sort/filters). The
 *                hook derives the own + member queries from it; do NOT pass
 *                `scope` — it's set internally.
 */
export function useOwnerMemberContracts(
  params: ListContractsParams,
): OwnerMemberContractsResult {
  const ownParams = useMemo<ListContractsParams>(
    () => ({ ...params, scope: "own" }),
    [params],
  );
  const own = useContractsListQuery(ownParams);

  const memberParams = useMemo<ListContractsParams>(
    () => ({
      ...params,
      scope: "member",
      page: 1,
      pageSize: MEMBER_PAGE_SIZE,
    }),
    [params],
  );
  const member = useContractsListQuery(memberParams);

  const sortKey = params.sortBy ?? "contractCode";
  const sortDir = params.sortDir ?? "desc";

  return useMemo<OwnerMemberContractsResult>(() => {
    const memberItems = member.data?.items ?? [];
    const merged = mergeOwnerMember<ContractListItem>({
      ownItems: own.data?.items ?? [],
      ownTotal: own.data?.total ?? 0,
      memberItems,
      memberTotal: member.data?.total ?? memberItems.length,
      getId: (r) => r.contractId,
      compare: compareBy(sortKey, sortDir),
    });
    return {
      ...merged,
      // Own is the spine: its pending/error drives the list. The member
      // overlay is additive and best-effort.
      isPending: own.isPending,
      isError: own.isError,
    };
  }, [own.data, member.data, own.isPending, own.isError, sortKey, sortDir]);
}
